<div class="evaluation-page">
  <div class="container py-3">
    <div class="page-header d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
      <div>
        <h2 class="page-title mb-1">Employees Evaluation</h2>
        <p class="page-subtitle mb-0">Quarterly appraisals for housekeeping, physiotherapy, and reception</p>
      </div>
      <button type="button" class="btn btn-outline-primary" (click)="load()">Refresh</button>
    </div>

    <div class="row g-3 mb-4">
      <div class="col-sm-6 col-lg-3">
        <div class="stat-card">
          <span class="stat-label">All</span>
          <span class="stat-value">{{ items.length }}</span>
        </div>
      </div>
      <div class="col-sm-6 col-lg-3">
        <div class="stat-card">
          <span class="stat-label">Waiting for review</span>
          <span class="stat-value">{{ pendingCount }}</span>
        </div>
      </div>
      <div class="col-sm-6 col-lg-3">
        <div class="stat-card">
          <span class="stat-label">Edit requested</span>
          <span class="stat-value">{{ editCount }}</span>
        </div>
      </div>
      <div class="col-sm-6 col-lg-3">
        <div class="stat-card">
          <span class="stat-label">Approved</span>
          <span class="stat-value">{{ approvedCount }}</span>
        </div>
      </div>
    </div>

    @if (apiMissing) {
      <div class="alert alert-warning">
        The evaluation API is not available on the server yet. This page is ready for
        <code>evaluations/</code> once that service is connected.
      </div>
    }

    <div class="card filter-card mb-4">
      <div class="card-body py-3">
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <label class="form-label small text-muted mb-1" for="evaluation-search">Search employee</label>
            <input
              id="evaluation-search"
              type="search"
              class="form-control"
              placeholder="Search by name..."
              [(ngModel)]="searchTerm"
              (ngModelChange)="onFilterChange()"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1" for="evaluation-status">Status</label>
            <select
              id="evaluation-status"
              class="form-select"
              [(ngModel)]="statusFilter"
              (ngModelChange)="onFilterChange()"
            >
              <option value="all">All statuses</option>
              @for (status of statuses; track status.value) {
                <option [value]="status.value">{{ status.label }}</option>
              }
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1" for="evaluation-type">Form</label>
            <select
              id="evaluation-type"
              class="form-select"
              [(ngModel)]="typeFilter"
              (ngModelChange)="onFilterChange()"
            >
              <option value="all">All forms</option>
              @for (type of formTypes; track type.value) {
                <option [value]="type.value">{{ type.label }}</option>
              }
            </select>
          </div>
          <div class="col-md-2">
            <button type="button" class="btn btn-outline-secondary w-100" (click)="searchTerm = ''; statusFilter = 'all'; typeFilter = 'all'; onFilterChange()">
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card table-card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span>Evaluation submissions</span>
        @if (filteredItems.length > 0) {
          <span class="badge rounded-pill bg-light text-dark">{{ filteredItems.length }} record(s)</span>
        }
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover evaluation-table mb-0">
            <thead>
              <tr>
                <th scope="col" class="col-num">#</th>
                <th scope="col">Employee</th>
                <th scope="col">Form</th>
                <th scope="col">Quarter</th>
                <th scope="col">Result</th>
                <th scope="col">Status</th>
                <th scope="col">Updated</th>
                <th scope="col" class="text-end col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              @if (filteredItems.length === 0) {
                <tr>
                  <td colspan="8" class="empty-state">
                    <div class="empty-content">
                      <p class="mb-1 fw-semibold">No evaluations found</p>
                      <p class="text-muted small mb-0">Submissions from employee profiles will show up here.</p>
                    </div>
                  </td>
                </tr>
              }
              @for (item of paginatedItems; track item.id; let i = $index) {
                <tr>
                  <td class="text-muted">{{ (page - 1) * pageSize + i + 1 }}</td>
                  <td class="fw-medium">{{ item.employee_name || '—' }}</td>
                  <td>{{ formTypeLabel(item.form_type) }}</td>
                  <td>{{ quarterLabel(item.period_from, item.period_to) }}</td>
                  <td>{{ resultLabel(item.form_type, item.answers) }}</td>
                  <td>
                    <span class="badge {{ statusMeta(item.status).badge }}">{{ statusMeta(item.status).label }}</span>
                  </td>
                  <td>{{ (item.updated_at || item.created_at) | date: 'dd/MM/yyyy' }}</td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-primary" (click)="openDetail(detailModal, item)">
                      Review
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      @if (filteredItems.length > pageSize) {
        <div class="card-footer bg-white border-top-0 pt-0 pb-3">
          <ngb-pagination
            class="mb-0"
            [collectionSize]="filteredItems.length"
            [(page)]="page"
            [pageSize]="pageSize"
            [maxSize]="5"
            [rotate]="true"
            [boundaryLinks]="true"
          ></ngb-pagination>
        </div>
      }
    </div>
  </div>
</div>

<ng-template #detailModal let-modal>
  <div class="modal-header border-0 pb-0">
    <div>
      <h4 class="modal-title mb-1">{{ selected?.employee_name || 'Evaluation' }}</h4>
      <p class="text-muted small mb-0">
        {{ formTypeLabel(draft.form_type || selected?.form_type) }}
        ·
        <span class="badge {{ statusMeta(selected?.status).badge }}">{{ statusMeta(selected?.status).label }}</span>
      </p>
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body pt-3">
    @if (selected?.admin_comment && detailMode === 'view' && !reviewAction) {
      <div class="alert alert-light border">
        <div class="small text-muted mb-1">Last admin comment</div>
        {{ selected?.admin_comment }}
      </div>
    }

    <div class="row g-3 mb-3">
      <div class="col-md-4">
        <label class="form-label" for="admin-form-type">Employee type</label>
        @if (detailMode === 'edit') {
          <select
            id="admin-form-type"
            class="form-select"
            [ngModel]="draft.form_type"
            (ngModelChange)="onFormTypeChange($event)"
          >
            @for (type of formTypes; track type.value) {
              <option [value]="type.value">{{ type.label }}</option>
            }
          </select>
        } @else {
          <input
            id="admin-form-type"
            type="text"
            class="form-control"
            [value]="formTypeLabel(draft.form_type)"
            readonly
          />
        }
      </div>
      <div class="col-md-4">
        <label class="form-label" for="admin-year">Year</label>
        <select
          id="admin-year"
          class="form-select"
          [(ngModel)]="draft.year"
          [disabled]="detailMode !== 'edit'"
        >
          @for (year of quarterYears; track year) {
            <option [ngValue]="year">{{ year }}</option>
          }
        </select>
      </div>
      <div class="col-md-4">
        <label class="form-label" for="admin-quarter">Quarter</label>
        <select
          id="admin-quarter"
          class="form-select"
          [(ngModel)]="draft.quarter"
          [disabled]="detailMode !== 'edit'"
        >
          @for (quarter of quarters; track quarter.value) {
            <option [ngValue]="quarter.value">{{ quarter.label }}</option>
          }
        </select>
      </div>
    </div>

    <app-evaluation-form
      [formType]="draft.form_type"
      [answers]="draft.answers"
      [viewOnly]="detailMode !== 'edit'"
      [staffName]="selected?.employee_name || ''"
      [year]="draft.year"
      [quarter]="draft.quarter"
      [status]="selected?.status || ''"
    ></app-evaluation-form>

    @if (reviewAction) {
      <div class="review-box mt-3">
        <label class="form-label" for="review-comment">{{ reviewTitle(reviewAction) }}</label>
        <textarea
          id="review-comment"
          class="form-control"
          rows="3"
          [(ngModel)]="reviewComment"
          [placeholder]="reviewAction === 'approve' ? 'Optional comment' : 'Tell the employee what needs to change'"
        ></textarea>
      </div>
    }
  </div>
  <div class="modal-footer border-0 pt-0 flex-wrap gap-2">
    @if (detailMode === 'edit') {
      <button type="button" class="btn btn-light" (click)="cancelEdit()" [disabled]="submitting">Cancel</button>
      <button type="button" class="btn btn-primary btn-add" (click)="saveEdit()" [disabled]="submitting">
        Save changes
      </button>
    } @else if (reviewAction) {
      <button type="button" class="btn btn-light" (click)="cancelReview()" [disabled]="submitting">Back</button>
      <button type="button" class="btn btn-primary btn-add" (click)="submitReview()" [disabled]="submitting">
        Confirm
      </button>
    } @else {
      <button type="button" class="btn btn-light" (click)="modal.dismiss()">Close</button>
      <button type="button" class="btn btn-outline-secondary" (click)="startEdit()">Edit</button>
      <button type="button" class="btn btn-outline-warning" (click)="beginReview('request_edit')">Ask for edit</button>
      <button type="button" class="btn btn-outline-danger" (click)="beginReview('reject')">Reject</button>
      <button type="button" class="btn btn-success" (click)="beginReview('approve')">Approve</button>
    }
  </div>
</ng-template>
